import React, { useState } from "react";
import { Button, Menu, MenuItem, Paper, Slide } from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { makeStyles } from "tss-react/mui";
import ColourConstants from "helpers/colourConstants";
import { ReactComponent as DeleteIcon } from "assets/icons/deleteIcon.svg";

const useStyles = makeStyles()(() => ({
	// Anchored to the viewport, not the table's scroll container, so it stays put
	// however far down a 400+ row list the selection was made.
	anchor: {
		position: "fixed",
		left: 0,
		right: 0,
		bottom: 0,
		zIndex: 1200,
		display: "flex",
		justifyContent: "center",
		pointerEvents: "none",
	},
	bar: {
		pointerEvents: "auto",
		display: "flex",
		alignItems: "center",
		gap: 16,
		margin: 16,
		padding: "10px 16px",
		borderRadius: 9,
		fontFamily: "Roboto Condensed",
	},
	count: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: 15,
		whiteSpace: "nowrap",
	},
	// Matches the row's popup menu rather than MUI's default button styling,
	// which would upper-case the label and swap the font.
	action: {
		fontFamily: "Roboto Condensed",
		fontWeight: "bold",
		fontSize: 15,
		textTransform: "none",
		color: ColourConstants.activeLink,
		whiteSpace: "nowrap",
	},
	destructive: { color: ColourConstants.deleteLink },
	destructiveIcon: {
		"& path": { fill: ColourConstants.deleteLink },
	},
	note: {
		fontFamily: "Roboto Condensed",
		fontSize: 15,
		color: ColourConstants.commonText,
		whiteSpace: "nowrap",
	},
	spacer: { flex: 1 },
}));

const BulkActionBar = ({
	selectedCount,
	taskCaptionPlural,
	onClear,
	actions,
	assignRelations = [],
	onAssign,
	disabledReason,
}) => {
	const { classes, cx } = useStyles();
	const { t } = useTranslation();
	const [assignAnchor, setAssignAnchor] = useState(null);

	return (
		<div className={classes.anchor} aria-live="polite">
			<Slide direction="up" in={selectedCount > 0} mountOnEnter unmountOnExit>
				<Paper elevation={8} className={classes.bar} role="toolbar">
					<span className={classes.count}>
						{t("label.countEntitySelected", { count: selectedCount, entity: taskCaptionPlural })}
					</span>
					{disabledReason ? (
						<span className={classes.note}>{disabledReason}</span>
					) : (
						<>
						{assignRelations.length > 0 && (
							<Button
								size="small"
								className={classes.action}
								endIcon={<ArrowDropDownIcon />}
								onClick={(event) => setAssignAnchor(event.currentTarget)}
							>
								{t("action.assign")}
							</Button>
						)}
						<Menu
							anchorEl={assignAnchor}
							open={Boolean(assignAnchor)}
							onClose={() => setAssignAnchor(null)}
						>
							{assignRelations.map((relation) => (
								<MenuItem
									key={relation.key}
									className={classes.action}
									onClick={() => {
										setAssignAnchor(null);
										onAssign(relation.key);
									}}
								>
									{relation.label}
								</MenuItem>
							))}
						</Menu>
						{actions.map(({ name, handler, isDestructive, disabled }) => (
							<Button
								key={name}
								size="small"
								onClick={handler}
								disabled={disabled}
								startIcon={
									isDestructive ? (
										<DeleteIcon className={classes.destructiveIcon} />
									) : undefined
								}
								className={cx(classes.action, {
									[classes.destructive]: isDestructive,
								})}
							>
								{name}
							</Button>
						))}
						</>
					)}
					<span className={classes.spacer} />
					<Button
						size="small"
						onClick={onClear}
						startIcon={<CloseIcon />}
						className={classes.action}
					>
						{t("action.clear")}
					</Button>
				</Paper>
			</Slide>
		</div>
	);
};

export default BulkActionBar;
